// client/src/pages/ReportsPage.jsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, AlertTriangle, Clock, HelpCircle, ChevronRight, TrendingUp } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../context/AuthContext'
import { useComplianceReport, useCostsReport } from '../api/hooks'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(value) {
  return `£${Number(value || 0).toFixed(2)}`
}

function fmtMonth(yyyymm) {
  if (!yyyymm) return '–'
  const [y, m] = yyyymm.split('-')
  return new Date(y, m - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-1.5 py-0.5 rounded">
      {registration}
    </span>
  )
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
        active
          ? 'bg-indigo-600/15 text-indigo-400'
          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)]'
      )}
    >
      {children}
    </button>
  )
}

// ─── Compliance section ───────────────────────────────────────────────────────

function ComplianceSection({ navigate }) {
  const { data: vehicles = [], isLoading } = useComplianceReport()

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (!vehicles.length) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
        <p className="text-sm text-[var(--text-muted)]">No vehicles found.</p>
      </div>
    )
  }

  const totalSchedules = vehicles.reduce((s, v) => s + Number(v.total), 0)
  const totalOverdue   = vehicles.reduce((s, v) => s + Number(v.overdue), 0)
  const totalDueSoon   = vehicles.reduce((s, v) => s + Number(v.due_soon), 0)
  const totalOk        = vehicles.reduce((s, v) => s + Number(v.up_to_date), 0)

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      {totalSchedules > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total schedules', value: totalSchedules, colour: 'text-[var(--text-primary)]', bg: 'bg-[var(--bg-surface)]' },
            { label: 'Overdue',   value: totalOverdue,  colour: 'text-red-400',   bg: 'bg-red-500/10'   },
            { label: 'Due soon',  value: totalDueSoon,  colour: 'text-amber-400', bg: 'bg-amber-500/10' },
            { label: 'Up to date', value: totalOk,      colour: 'text-green-400', bg: 'bg-green-500/10' },
          ].map(({ label, value, colour, bg }) => (
            <div key={label} className={clsx('rounded-xl border border-[var(--border)] p-4', bg)}>
              <p className={clsx('font-display text-3xl font-bold', colour)}>{value}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Per vehicle */}
      <div className="space-y-3">
        {vehicles.map(v => {
          const total    = Number(v.total)
          const overdue  = Number(v.overdue)
          const dueSoon  = Number(v.due_soon)
          const ok       = Number(v.up_to_date)
          const noDate   = Number(v.no_date)

          const overallStatus =
            overdue  > 0 ? 'overdue'
            : dueSoon > 0 ? 'due-soon'
            : ok      > 0 ? 'ok'
            : 'unknown'

          const statusConfig = {
            overdue:    { icon: AlertTriangle, colour: 'text-red-400',   border: 'border-red-500/30',   label: 'Action needed' },
            'due-soon': { icon: Clock,         colour: 'text-amber-400', border: 'border-amber-500/30', label: 'Due soon'      },
            ok:         { icon: CheckCircle2,  colour: 'text-green-400', border: 'border-green-500/30', label: 'Up to date'    },
            unknown:    { icon: HelpCircle,    colour: 'text-[var(--text-muted)]', border: 'border-[var(--border)]', label: 'No data' },
          }[overallStatus]

          const StatusIcon = statusConfig.icon

          return (
            <div key={v.id} className={clsx(
              'bg-[var(--bg-surface)] border rounded-xl p-5',
              statusConfig.border
            )}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <Plate registration={v.registration} />
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {v.make} {v.model} {v.year ? `(${v.year})` : ''}
                    </p>
                    {v.current_mileage && (
                      <p className="text-xs text-[var(--text-muted)]">
                        {Number(v.current_mileage).toLocaleString()} miles
                      </p>
                    )}
                  </div>
                </div>
                <div className={clsx('flex items-center gap-1.5 shrink-0', statusConfig.colour)}>
                  <StatusIcon size={14} />
                  <span className="text-xs font-medium">{statusConfig.label}</span>
                </div>
              </div>

              {total > 0 ? (
                <>
                  {/* Progress bar */}
                  <div className="flex h-2 rounded-full overflow-hidden bg-[var(--bg-base)] mb-3">
                    {overdue  > 0 && <div className="bg-red-400"   style={{ width: `${(overdue  / total) * 100}%` }} />}
                    {dueSoon  > 0 && <div className="bg-amber-400" style={{ width: `${(dueSoon  / total) * 100}%` }} />}
                    {ok       > 0 && <div className="bg-green-400" style={{ width: `${(ok       / total) * 100}%` }} />}
                    {noDate   > 0 && <div className="bg-[var(--text-muted)] opacity-30" style={{ width: `${(noDate / total) * 100}%` }} />}
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {overdue > 0 && (
                      <span className="text-xs text-red-400">{overdue} overdue</span>
                    )}
                    {dueSoon > 0 && (
                      <span className="text-xs text-amber-400">{dueSoon} due soon</span>
                    )}
                    {ok > 0 && (
                      <span className="text-xs text-green-400">{ok} up to date</span>
                    )}
                    {noDate > 0 && (
                      <span className="text-xs text-[var(--text-muted)]">{noDate} no date set</span>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-xs text-[var(--text-muted)]">No service schedules configured.</p>
              )}

              <button
                onClick={() => navigate(`/vehicles/${v.id}`)}
                className="mt-4 flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                View schedules <ChevronRight size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Customer cost section ────────────────────────────────────────────────────

function CustomerCostSection() {
  const { data, isLoading } = useCostsReport()
  const records = data?.records || []

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (!records.length) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
        <p className="text-sm text-[var(--text-muted)]">No service costs recorded yet.</p>
      </div>
    )
  }

  // Aggregate by month
  const byMonth = {}
  for (const r of records) {
    const m = r.month || 'Unknown'
    if (!byMonth[m]) byMonth[m] = { month: m, total: 0, items: [] }
    byMonth[m].total += Number(r.cost || 0)
    byMonth[m].items.push(r)
  }
  const months = Object.values(byMonth).sort((a, b) => b.month.localeCompare(a.month))

  const grandTotal = records.reduce((s, r) => s + Number(r.cost || 0), 0)
  const maxMonth   = Math.max(...months.map(m => m.total))

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4">
          <p className="font-display text-2xl font-bold text-[var(--text-primary)]">{fmt(grandTotal)}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Total recorded spend</p>
        </div>
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4">
          <p className="font-display text-2xl font-bold text-[var(--text-primary)]">{months.length}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Months with records</p>
        </div>
      </div>

      {/* Monthly breakdown */}
      <div className="space-y-3">
        {months.map(({ month, total, items }) => (
          <div key={month} className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
              <p className="text-sm font-medium text-[var(--text-primary)]">{fmtMonth(month)}</p>
              <div className="flex items-center gap-3">
                {/* Mini bar */}
                <div className="w-24 h-1.5 bg-[var(--bg-base)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{ width: `${maxMonth > 0 ? (total / maxMonth) * 100 : 0}%` }}
                  />
                </div>
                <p className="text-sm font-semibold text-[var(--text-primary)] tabular-nums w-20 text-right">
                  {fmt(total)}
                </p>
              </div>
            </div>
            <div className="divide-y divide-[var(--border)]">
              {items.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Plate registration={r.registration} />
                    <span className="text-sm text-[var(--text-secondary)] truncate">{r.service_name}</span>
                  </div>
                  <span className="text-sm text-[var(--text-primary)] tabular-nums shrink-0 ml-3">
                    {fmt(r.cost)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Garage revenue section ───────────────────────────────────────────────────

function GarageRevenueSection() {
  const { data, isLoading } = useCostsReport()
  const months = data?.months || []

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (!months.length) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
        <p className="text-sm text-[var(--text-muted)]">No invoices found.</p>
      </div>
    )
  }

  const totalRevenue = months.reduce((s, m) => s + Number(m.revenue || 0), 0)
  const totalPaid    = months.reduce((s, m) => s + Number(m.paid_revenue || 0), 0)
  const maxRevenue   = Math.max(...months.map(m => Number(m.revenue || 0)))

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4">
          <p className="font-display text-2xl font-bold text-[var(--text-primary)]">{fmt(totalRevenue)}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Total invoiced ({months.length} months)</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
          <p className="font-display text-2xl font-bold text-green-400">{fmt(totalPaid)}</p>
          <p className="text-xs text-green-400/70 mt-1">Total collected</p>
        </div>
      </div>

      {/* Monthly rows */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-5 py-2.5 border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)]">
          <span>Month</span>
          <span className="text-right">Invoices</span>
          <span className="text-right">Invoiced</span>
          <span className="text-right">Collected</span>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {months.map((m) => {
            const revenue = Number(m.revenue || 0)
            const paid    = Number(m.paid_revenue || 0)
            const pct     = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0

            return (
              <div key={m.month} className="px-5 py-3">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 items-center mb-1.5">
                  <span className="text-sm font-medium text-[var(--text-primary)]">{fmtMonth(m.month)}</span>
                  <span className="text-sm text-[var(--text-secondary)] text-right tabular-nums">{m.invoice_count}</span>
                  <span className="text-sm text-[var(--text-primary)] text-right tabular-nums">{fmt(revenue)}</span>
                  <span className="text-sm text-green-400 text-right tabular-nums">{fmt(paid)}</span>
                </div>
                <div className="h-1.5 bg-[var(--bg-base)] rounded-full overflow-hidden">
                  <div className="h-full flex">
                    {paid > 0 && (
                      <div className="bg-green-400" style={{ width: `${(paid / revenue) * pct}%` }} />
                    )}
                    {revenue - paid > 0 && (
                      <div className="bg-indigo-400 opacity-40" style={{ width: `${((revenue - paid) / revenue) * pct}%` }} />
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const navigate    = useNavigate()
  const { user }    = useAuth()
  const isCustomer  = user?.role === 'customer'
  const isGarage    = ['garage_user', 'garage_admin', 'platform_admin'].includes(user?.role)

  const [tab, setTab] = useState('compliance')

  const customerTabs = [
    { id: 'compliance', label: 'Service Compliance' },
    { id: 'costs',      label: 'Cost History'       },
  ]
  const garageTabs = [
    { id: 'compliance', label: 'Fleet Compliance' },
    { id: 'revenue',    label: 'Revenue'          },
  ]
  const tabs = isCustomer ? customerTabs : garageTabs

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <TrendingUp size={24} className="text-indigo-400" />
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Reports</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-1 w-fit">
        {tabs.map(t => (
          <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </TabBtn>
        ))}
      </div>

      {/* Content */}
      {tab === 'compliance' && <ComplianceSection navigate={navigate} />}
      {tab === 'costs'      && <CustomerCostSection />}
      {tab === 'revenue'    && <GarageRevenueSection />}
    </div>
  )
}
